<template>
  <div class="min-h-screen bg-gradient-to-br from-navy-50 via-white to-primary-50">
    <!-- Hero section -->
    <div class="relative overflow-hidden">
      <!-- Background pattern -->
      <div class="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-700 to-navy-800"></div>
      <div class="absolute inset-0 bg-black/10"></div>
      
      <!-- Decorative elements -->
      <div class="absolute top-0 left-0 w-full h-full">
        <div class="absolute top-20 left-10 w-32 h-32 bg-gold-400/20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-20 right-10 w-40 h-40 bg-accent-400/20 rounded-full blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div class="text-center animate-fade-in">
          <h1 class="hero-title text-white mb-6">
            <span class="bg-gradient-to-r from-gold-400 to-gold-300 bg-clip-text text-transparent">Contact</span>
          </h1>
          <p class="hero-subtitle text-white/90 max-w-3xl mx-auto">
            N'hésitez pas à nous contacter pour toute question ou information
          </p>
        </div>
      </div>
    </div>
    
    <!-- Contact content -->
    <div class="py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- Contact form -->
          <div>
            <h2 class="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>
            
            <form @submit.prevent="submitForm" class="space-y-6">
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="name"
                  v-model="form.name"
                  required
                  class="form-input"
                  :class="{ 'border-red-500 ring-red-500/50': errors.name }"
                />
                <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
              </div>
              
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email *
                </label>
                <input
                  type="email"
                  id="email"
                  v-model="form.email"
                  required
                  class="form-input"
                  :class="{ 'border-red-500 ring-red-500/50': errors.email }"
                />
                <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
              </div>
              
              <div>
                <label for="subject" class="block text-sm font-medium text-gray-700 mb-2">
                  Sujet *
                </label>
                <input
                  type="text"
                  id="subject"
                  v-model="form.subject"
                  required
                  class="form-input"
                  :class="{ 'border-red-500 ring-red-500/50': errors.subject }"
                />
                <p v-if="errors.subject" class="mt-1 text-sm text-red-600">{{ errors.subject }}</p>
              </div>
              
              <div>
                <label for="message" class="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  v-model="form.message"
                  required
                  rows="6"
                  class="form-textarea"
                  :class="{ 'border-red-500 ring-red-500/50': errors.message }"
                ></textarea>
                <p v-if="errors.message" class="mt-1 text-sm text-red-600">{{ errors.message }}</p>
              </div>
              
              <!-- Honeypot field (hidden) -->
              <div style="display: none;">
                <label for="website">Website (leave empty):</label>
                <input 
                  type="text" 
                  id="website" 
                  name="website" 
                  v-model="form.honeypot"
                  tabindex="-1"
                  autocomplete="off"
                />
              </div>
              
              <button
                type="submit"
                :disabled="submitting"
                class="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="submitting" class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Envoi en cours...
                </span>
                <span v-else>Envoyer le message</span>
              </button>
            </form>
            
            <!-- Success message -->
            <div v-if="successMessage" class="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {{ successMessage }}
            </div>
          </div>
          
          <!-- Contact info -->
          <div>
            <h2 class="text-2xl font-bold text-gray-900 mb-6">Informations de contact</h2>
            
            <div class="space-y-6">
              <div class="flex items-start">
                <div class="flex-shrink-0">
                  <div class="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                </div>
              <div class="ml-4">
                <h3 class="text-lg font-semibold text-gray-900">Adresse</h3>
                <p class="text-gray-600">Basse Cour 16, 4970 Stavelot</p>
              </div>
              </div>
              
              <div class="flex items-start">
                <div class="flex-shrink-0">
                  <div class="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </div>
                </div>
                <div class="ml-4">
                  <h3 class="text-lg font-semibold text-gray-900">Email</h3>
                  <p class="text-gray-600">rhes@emulation.be</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="flex-shrink-0">
                  <div class="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                </div>
                <div class="ml-4">
                  <h3 class="text-lg font-semibold text-gray-900">Réponse</h3>
                  <p class="text-gray-600">Nous vous répondrons dans les plus brefs délais</p>
                </div>
              </div>
            </div>            
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { contactService } from '../services/contactService'

export default {
  name: 'Contact',
  data() {
    return {
      form: {
        name: '',
        email: '',
        subject: '',
        message: '',
        honeypot: ''
      },
      errors: {},
      submitting: false,
      successMessage: ''
    }
  },
  methods: {
    validateForm() {
      this.errors = {}
      
      if (!this.form.name.trim()) {
        this.errors.name = 'Le nom est requis'
      }
      
      if (!this.form.email.trim()) {
        this.errors.email = 'L\'email est requis'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.errors.email = 'L\'email n\'est pas valide'
      }
      
      if (!this.form.subject.trim()) {
        this.errors.subject = 'Le sujet est requis'
      }
      
      if (!this.form.message.trim()) {
        this.errors.message = 'Le message est requis'
      }
      
      return Object.keys(this.errors).length === 0
    },
    async submitForm() {
      if (!this.validateForm()) return
      
      this.submitting = true
      this.successMessage = ''
      
      try {
        // Préparer les données sans le honeypot pour l'API
        const { honeypot, ...formData } = this.form
        
        // Envoyer le honeypot dans les headers
        const headers = {
          'Content-Type': 'application/json',
          'x-honeypot': honeypot
        }
        
        await contactService.sendContactMessage(formData, headers)
        this.successMessage = 'Votre message a été envoyé avec succès ! Nous vous répondrons bientôt.'
        this.form = { name: '', email: '', subject: '', message: '', honeypot: '' }
      } catch (error) {
        console.error('Error sending contact message:', error)
        this.successMessage = 'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
